import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { buildBandwidthDashboardQueryParams, fetchBandwidthDashboard } from './bandwidthDashboardApi';
import type { BandwidthCrossFilterSlice } from './bandwidthCrossFilter';
import { bandwidthCrossFilterQueryKey } from './bandwidthCrossFilter';
import type { UsersChartDrill } from './bandwidthUsersDrill';
import { usersChartDrillQueryKey } from './bandwidthUsersDrill';

export interface UseBandwidthDashboardOptions {
  enabled?: boolean;
  /** Distinguishes parallel queries (e.g. base vs drill-down). */
  queryKeyExtra?: string;
  crossFilter?: BandwidthCrossFilterSlice | null;
  /** When true, sends assignment group + assignee page filters (Bandwidth Dashboard tab). */
  applyCountTimePageFilters?: boolean;
  /** When true, sends assignmentGroupId from filters (classic Tasks & Customers drill-down). */
  applyAssignmentGroupFromFilters?: boolean;
  /** When true, sends assignedToMe / assignedToUserId page filters. */
  applyAssigneePageFilters?: boolean;
  /** When true, sends assignee page filters only (Weekwise tab). Prefer applyAssigneePageFilters. */
  applyUsersPageFilters?: boolean;
  /** When false, loads all activity types (Bandwidth Dashboard tab). Default true. */
  applyTimeEntryTypeFilter?: boolean;
  /** Bandwidth Monthwise tab: cross-filter from bar clicks. */
  datesDrill?: { month: string | null; weekdayMonFirst: number | null };
  /** Bandwidth Weekwise tab: week / type / customer bar drill. */
  usersChartDrill?: UsersChartDrill | null;
}

export function useBandwidthDashboard(
  filterKey: string,
  filterValues: Record<string, string | string[]>,
  options?: UseBandwidthDashboardOptions
) {
  const extra = options?.queryKeyExtra ?? 'base';
  const cross = options?.crossFilter ?? null;
  const applyCt = options?.applyCountTimePageFilters === true;
  const applyAg = options?.applyAssignmentGroupFromFilters === true;
  const applyUsers = options?.applyUsersPageFilters === true;
  const applyAssignee =
    options?.applyAssigneePageFilters === true || applyCt || applyUsers;
  const applyTte = options?.applyTimeEntryTypeFilter !== false;
  const dd = options?.datesDrill;
  const drillKey =
    dd && (dd.month || dd.weekdayMonFirst != null)
      ? `dm:${dd.month ?? ''}|dw:${dd.weekdayMonFirst ?? ''}`
      : 'nodrill';
  const usersDrillKey = usersChartDrillQueryKey(options?.usersChartDrill ?? null);
  return useQuery({
    queryKey: [
      'bandwidth-dashboard',
      filterKey,
      extra,
      bandwidthCrossFilterQueryKey(cross),
      applyCt ? 'ctf' : applyAg ? 'agf' : applyUsers ? 'usersf' : 'base',
      applyAssignee ? 'asg' : 'noasg',
      applyTte ? 'tte' : 'notte',
      drillKey,
      usersDrillKey,
    ],
    queryFn: () =>
      fetchBandwidthDashboard(
        buildBandwidthDashboardQueryParams(filterValues, {
          crossFilter: cross,
          applyCountTimePageFilters: applyCt,
          applyAssignmentGroupFromFilters: applyAg,
          applyAssigneePageFilters: applyAssignee,
          applyUsersPageFilters: applyUsers,
          applyTimeEntryTypeFilter: applyTte,
          datesDrill: dd,
          usersChartDrill: options?.usersChartDrill ?? null,
        })
      ),
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
    enabled: options?.enabled !== false,
  });
}
